import React, { useState } from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { History } from 'lucide-react';
import { CostHistoryKind } from '../../api/costHistoryApi';
import { useCostHistory } from '../../hooks/costHistory/useCostHistory';
import { costChangeLines, costChangeSourceLabel } from './costHistoryView';
import './CostHistory.css';

interface CostHistoryButtonProps {
    kind: CostHistoryKind;
    /** Template ID (templates) or component type key (lifetimes) */
    subjectKey: string;
    /** Modal title, e.g. the template name */
    title: string;
}

/** Subtle "Historik" link next to a mallkostnad that opens its change history. */
export const CostHistoryButton: React.FC<CostHistoryButtonProps> = ({ kind, subjectKey, title }) => {
    const [show, setShow] = useState(false);

    return (
        <>
            <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted cost-history-button"
                onClick={() => setShow(true)}
            >
                <History size={13} className="me-1" />
                Historik
            </button>
            {show && (
                <CostHistoryModal kind={kind} subjectKey={subjectKey} title={title} onHide={() => setShow(false)} />
            )}
        </>
    );
};

interface CostHistoryModalProps extends CostHistoryButtonProps {
    onHide: () => void;
}

const CostHistoryModal: React.FC<CostHistoryModalProps> = ({ kind, subjectKey, title, onHide }) => {
    const { entries, loading, error, userName, buildingName } = useCostHistory(kind, subjectKey, true);

    return (
        <Modal show onHide={onHide} centered className="cost-history-modal">
            <Modal.Header closeButton>
                <Modal.Title as="h5">Kostnadshistorik – {title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" role="status" />
                    </div>
                ) : !error && entries.length === 0 ? (
                    <p className="text-muted fst-italic mb-0">Inga ändringar loggade ännu.</p>
                ) : (
                    <ul className="cost-history-list">
                        {entries.map(entry => (
                            <li key={entry.key} className="cost-history-entry">
                                <div className="cost-history-meta">
                                    <span>{entry.changedWhen.slice(0, 16)}</span>
                                    <span>{userName(entry.changedBy)}</span>
                                </div>
                                {costChangeLines(entry).map(line => (
                                    <div key={line.label} className="cost-history-line">
                                        <span className="cost-history-label">{line.label}</span>
                                        <span>{line.from} → <strong>{line.to}</strong></span>
                                    </div>
                                ))}
                                <div className="cost-history-source">
                                    {costChangeSourceLabel(entry.source, entry.buildingKey ? buildingName(entry.buildingKey) : undefined)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Modal.Body>
        </Modal>
    );
};
